import { getUrl } from 'aws-amplify/storage'
import { getPortalEnvironment } from '@/config/portalEnvironment'
import { parseAwsJson } from '@/utils/graphql'

/**
 * Lead list APIs (getLeadShard / getTaskLeads) now return S3 object keys only.
 * The Cognito Identity Pool authenticated role can GetObject on those buckets;
 * the client downloads shard JSON with Amplify Storage and joins locally.
 */

function resolveRegionForBucket(bucketName) {
  const env = getPortalEnvironment()
  const bucket = String(bucketName ?? '').trim()
  if (!bucket) return env.s3Region
  if (bucket === env.kbS3Bucket) return env.kbS3Region
  if (bucket === env.s3Bucket) return env.s3Region
  // Lead shards live in the org data region (same as the default Amplify bucket).
  return env.s3Region
}

/**
 * Download and parse a lead-shard object from S3.
 * Accepts JSON array, { items|leads|records }, or NDJSON.
 */
export async function downloadLeadShardJson(bucketName, objectKey) {
  const bucket = String(bucketName ?? '').trim()
  const path = String(objectKey ?? '').trim().replace(/^\//, '')
  if (!bucket || !path) return []

  // A freshly presigned URL every call (unique query-string signature) plus
  // an explicit no-store fetch — shard files were previously fetched from a
  // stable URL with no Cache-Control, so browsers heuristically cached them
  // and deletes/edits kept showing stale lists even after a page Refresh.
  const { url } = await getUrl({
    path,
    options: {
      bucket: {
        bucketName: bucket,
        region: resolveRegionForBucket(bucket),
      },
      expiresIn: 300,
    },
  })

  const response = await fetch(url, { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Shard download failed (${response.status})`)
  }
  const text = await response.text()
  return parseLeadShardPayload(text)
}

export function parseLeadShardPayload(raw) {
  if (raw == null) return []

  if (Array.isArray(raw)) return raw.filter((item) => item && typeof item === 'object')

  if (typeof raw === 'object') {
    const list =
      (Array.isArray(raw.items) && raw.items) ||
      (Array.isArray(raw.leads) && raw.leads) ||
      (Array.isArray(raw.records) && raw.records) ||
      (Array.isArray(raw.data) && raw.data) ||
      []
    return list.filter((item) => item && typeof item === 'object')
  }

  const text = String(raw).trim()
  if (!text) return []

  const parsed = parseAwsJson(text)
  if (parsed != null) return parseLeadShardPayload(parsed)

  // NDJSON fallback
  const rows = []
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed) continue
    const row = parseAwsJson(trimmed)
    if (row && typeof row === 'object' && !Array.isArray(row)) rows.push(row)
  }
  return rows
}

function requestIdOf(lead) {
  if (!lead || typeof lead !== 'object') return ''
  return String(
    lead.request_id ??
      lead.requestId ??
      lead.id ??
      lead.uuid ??
      '',
  ).trim()
}

/** True when GraphQL data is the new S3-key envelope (not inline lead items). */
export function isLeadShardKeyPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false
  if (Array.isArray(data.items) && data.items.length) return false
  return Boolean(
    String(data.shard_key ?? data.shardKey ?? '').trim() ||
      (Array.isArray(data.shard_keys) && data.shard_keys.length) ||
      (Array.isArray(data.shardKeys) && data.shardKeys.length),
  )
}

/**
 * Resolve getLeadShard SUCCESS data → lead objects.
 * New: { bucket, shard_key, shard_index, total_shards }
 * Legacy: { items, shard_index, total_shards }
 */
export async function resolveLeadShardItems(data) {
  if (!data || typeof data !== 'object') return { items: [], meta: {} }

  if (Array.isArray(data.items)) {
    return {
      items: data.items,
      meta: {
        shard_index: data.shard_index ?? data.shardIndex,
        total_shards: data.total_shards ?? data.totalShards,
      },
    }
  }

  const bucket = String(data.bucket ?? '').trim()
  const shardKey = String(data.shard_key ?? data.shardKey ?? '').trim()
  if (!bucket || !shardKey) {
    return {
      items: [],
      meta: {
        shard_index: data.shard_index ?? data.shardIndex,
        total_shards: data.total_shards ?? data.totalShards,
      },
    }
  }

  const items = await downloadLeadShardJson(bucket, shardKey)
  return {
    items,
    meta: {
      bucket,
      shard_key: shardKey,
      shard_index: data.shard_index ?? data.shardIndex,
      total_shards: data.total_shards ?? data.totalShards,
    },
  }
}

/**
 * Resolve getTaskLeads SUCCESS data → joined lead list + campaign record.
 * GraphQL payload: { bucket, shard_keys, membership, task }
 */
export async function resolveCampaignLeadsPayload(data) {
  const resolved = await resolveTaskLeadsPayload(data)
  return {
    items: resolved.items,
    campaign: resolved.task ?? null,
  }
}

/**
 * @deprecated Use resolveCampaignLeadsPayload — kept for internal shard join.
 */
export async function resolveTaskLeadsPayload(data) {
  if (!data || typeof data !== 'object') {
    return { items: [], task: null }
  }

  // Legacy full-lead responses
  if (Array.isArray(data)) {
    return { items: data, task: null }
  }
  if (Array.isArray(data.items) && !isLeadShardKeyPayload(data)) {
    return { items: data.items, task: data.task ?? null }
  }

  const bucket = String(data.bucket ?? '').trim()
  const shardKeys = (
    Array.isArray(data.shard_keys)
      ? data.shard_keys
      : Array.isArray(data.shardKeys)
        ? data.shardKeys
        : []
  )
    .map((key) => String(key ?? '').trim())
    .filter(Boolean)

  const membership = (
    Array.isArray(data.membership) ? data.membership : []
  ).filter((row) => row && typeof row === 'object')

  const leadByRequestId = new Map()

  if (bucket && shardKeys.length) {
    const shardLists = await Promise.all(
      shardKeys.map((key) => downloadLeadShardJson(bucket, key)),
    )
    for (const leads of shardLists) {
      for (const lead of leads) {
        const id = requestIdOf(lead)
        if (id) leadByRequestId.set(id, lead)
      }
    }
  }

  // Membership is the source of truth for which leads belong to the task.
  const items = membership.map((row) => {
    const requestId = String(row.request_id ?? row.requestId ?? '').trim()
    const assigned = String(
      row.task_assigned_to ??
        row.taskAssignedTo ??
        row.assigned_agent_id ??
        row.assignedAgentId ??
        '',
    ).trim()
    const lead = (requestId && leadByRequestId.get(requestId)) || {}
    return {
      ...lead,
      request_id: requestId || requestIdOf(lead),
      task_assigned_to: assigned,
      assigned_agent_id: assigned,
      // Dial state is per-campaign, so the membership row wins over the
      // lead's own cross-campaign copy of the same fields -- matching what
      // getTaskLeads already returns from the server. Only applied when the
      // row actually carries them, so an older response still falls back to
      // the lead's values rather than blanking the row out.
      ...(row.request_status !== undefined ? { request_status: row.request_status } : {}),
      ...(row.next_reminder_due_on !== undefined ? { next_reminder_due_on: row.next_reminder_due_on } : {}),
      ...(row.dial_attempt_count !== undefined ? { dial_attempt_count: row.dial_attempt_count } : {}),
      ...(row.last_ai_dial_attempt_on !== undefined ? { last_ai_dial_attempt_on: row.last_ai_dial_attempt_on } : {}),
      ...(row.callable_again !== undefined ? { callable_again: row.callable_again } : {}),
      ...(row.status_updated_on !== undefined ? { status_updated_on: row.status_updated_on } : {}),
    }
  })

  return {
    items,
    task: data.task ?? null,
  }
}
